import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Popover } from "antd";
import { cn } from "../utils/lib";
import { ReactComponent as User } from "../assets/SVG/person.svg";
import { ReactComponent as Cog } from "../assets/SVG/cog.svg";
import { ReactComponent as Notification } from "../assets/SVG/notifications.svg";
import { useUserContext } from "../Context/UserContext";
import { usePageTitle } from "../Context/PageTitleContext";
import { signOut } from "../utils/signOut";

export default function Header() {
  const { pathname } = useLocation();
  const [user, setUser] = useUserContext();
  const [pageTitle, setPageTitle] = usePageTitle();

  // A page-set title (e.g. a company name replacing its numeric id) only
  // applies to the page that set it — clear it on every route change so the
  // next page starts from the raw path unless it sets its own.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageTitle(null), [pathname]);

  const handleSignOut = () => {
    signOut({ setUser });
  };

  const displayName =
    (user?.user?.name || "").trim() ||
    user?.user?.email ||
    user?.user?.username ||
    "Signed in";
  const displayEmail = user?.user?.email || "";

  return (
    <div className="relative z-20 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-3 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden">
        <span className="shrink-0 text-sm font-medium text-slate-500">Pages</span>
        <span className="shrink-0 font-semibold text-slate-400">/</span>
        <span
          className="min-w-0 truncate text-lg font-semibold capitalize text-slate-700"
          title={pageTitle || pathname.slice(1)}
        >
          {pageTitle || pathname.slice(1)}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div className="min-w-[10rem] max-w-[16rem] py-0.5">
              <p className="m-0 text-xs font-medium uppercase tracking-wide text-slate-500">
                Signed in as
              </p>
              <p className="m-0 mt-1 truncate text-sm font-semibold text-slate-800">
                {displayName}
              </p>
              {displayEmail && displayEmail !== displayName ? (
                <p className="m-0 mt-0.5 truncate text-xs text-slate-500">
                  {displayEmail}
                </p>
              ) : null}
            </div>
          }
        >
          <button
            type="button"
            aria-label="Account"
            className="rounded-md p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
          >
            <User className="h-5 w-5" />
          </button>
        </Popover>
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded-md px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 hover:text-slate-900"
        >
          Sign Out
        </button>
        <button
          type="button"
          className="rounded-md p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
        >
          <Cog className="h-4 w-4" />
        </button>
        <Link
          to="/notifications"
          className="rounded-md p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800"
          aria-label="Notifications"
        >
          <Notification className="h-5 w-5" />
        </Link>
      </div>
    </div>
  );
}

// eslint-disable-next-line no-unused-vars
const NavLink = ({ to, children }) => {
  const { pathname } = useLocation();
  return (
    <Link
      to={to}
      className={cn("rounded-md px-5 py-4 font-medium leading-normal", {
        "bg-golden text-white": pathname === to,
      })}
    >
      {children}
    </Link>
  );
};
